import getBlogImageUrl from "@Pages/Blog/utils/getBlogImageUrl";
import { BLOG_REGIONS, getBlogRegion } from "../constants";
import { BLOG_REGION_COUNTRY_SLUGS } from "../settings";
import type { BlogRegion } from "../types";

export type CmsBlogCountry = {
  slug?: string;
  name?: string | null;
  regionDescription?: string | null;
  blogHomeOrder?: number | null;
  showOnBlogHome?: boolean | null;
  isArchived?: boolean | null;
  logo?: { url?: string | null } | null;
};

/** Legacy home order: configured four first, then any other live CMS countries A–Z. */
export const buildFallbackHomeRegionOrder = (countrySlugsWithArticles: string[]): string[] => {
  const withArticles = new Set(countrySlugsWithArticles);
  const knownFirst = BLOG_REGION_COUNTRY_SLUGS.filter((slug) => withArticles.has(slug));
  const rest = [...withArticles].filter((slug) => !BLOG_REGION_COUNTRY_SLUGS.includes(slug)).sort();
  return [...knownFirst, ...rest];
};

export const blogRegionFromCountrySlug = (
  countrySlug: string,
  cmsCountry?: CmsBlogCountry | null,
): BlogRegion => {
  const staticRegion = BLOG_REGIONS.find((region) => region.countrySlug === countrySlug);
  if (staticRegion) return staticRegion;

  const name = cmsCountry?.name?.trim() || countrySlug;
  const logoUrl = cmsCountry?.logo?.url?.trim();
  return {
    slug: countrySlug,
    countrySlug,
    name,
    nameKey: "",
    descriptionKey: "",
    flag: logoUrl ? getBlogImageUrl(logoUrl) : "",
  };
};

export const blogRegionsFromCountrySlugs = (
  countrySlugs: string[],
  cmsBySlug: Map<string, CmsBlogCountry>,
): BlogRegion[] =>
  countrySlugs
    .map((countrySlug) => blogRegionFromCountrySlug(countrySlug, cmsBySlug.get(countrySlug)))
    .filter((region): region is BlogRegion => !!region.slug && !!region.countrySlug);

/** Resolve `/blog/region/:pageSlug` to a region model (static URL aliases + CMS countries). */
export const resolveBlogRegionByPageSlug = (
  pageSlug: string | undefined,
  cmsCountry?: CmsBlogCountry | null,
): BlogRegion | null => {
  if (!pageSlug?.trim()) return null;

  const fromStaticPage = getBlogRegion(pageSlug);
  if (fromStaticPage) return fromStaticPage;

  const fromStaticCountry = BLOG_REGIONS.find((region) => region.countrySlug === pageSlug);
  if (fromStaticCountry) return fromStaticCountry;

  if (cmsCountry?.slug && cmsCountry.isArchived !== true) {
    return blogRegionFromCountrySlug(cmsCountry.slug, cmsCountry);
  }

  return null;
};
